import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { MainContent } from '../../global/components/layout/main-content-container';
import { PageContainer } from '../../global/components/layout/page-container';
import { getMyCpgPodInfo } from '../../global/ducks/mycpg-pod-api';
import { TcsmStates } from '../../root/tcsm-model';
import { MyCpgPodsSettings, MyCpgPodsState } from './mycpg-pod-models';
import { setPods, setPodsError } from './mycpg-pod-slice';
import { getTcsmEpiProps, setPodsSettings } from '../../global/ducks/epi-api';
import { MyCpgHero } from './mycpg-landing-hero';
import { useQuery } from '../../global/hooks/useQuery';
import { MyCpgPodsDetailPage } from './mycpg-pods-detail-page';
import { PortalGuidesAndResources } from '../arc/features/map-landing/components/portal-guides-resources';
import { WysiwygWithReactLink } from '../../global/components/wysiwyg-with-react-link';
import { DashboardNotifications } from '../arc/features/map-landing/components/dashboard-notifications';
import { useBasePath } from '../../global/hooks/useBasePath';
import { AccountInfoRoutes } from '../account-info/constants/routes';
import { ArcRoutes } from '../arc/constants/routes';
import { PortalSidebar } from '../arc/features/map-landing/components/portal-sidebar';
import { useEpiPageContent } from '../../global/hooks/useEpiPageContent';
import { TcsmNewsUpdatesPage } from '../../global/models/tcsm-page-models';
import { useDemographicsPerson } from '../account-info/hooks/useDemographicsPerson';
import { DemographicsContext } from '../../global/models/demographics-model';
import { ClearTcsmSessionStorage } from '../../global/helpers/session-storage-helper';
import { DashboardUser, TcsmDashboardUsers } from '../../root/tcsm-dashboard-users';
import { useDeserializedToken } from '../arc/components/use-deserialized-token';
import { TcsmSiteSettingsProps } from '../../global/models/epi-model';
import { PortalLandingBanner } from '../arc/features/map-landing/components/portal-landing-banner';
import { replaceAll } from '../../global/helpers/string-helpers';
import { RenderPods } from './pods-components/pods-render';
import { getNotifications } from '../../global/ducks/notification-prc-api';
import { setNotifications } from '../notification/notification-slice';
import { apiErrorClear } from '../../global/ducks/base-api';
import { NotificationStatus } from '../notification/notification-models';
import { TcsmAppConfigs } from '../../global/models/configuration-models';
import { useMemberGHLEmploymentId } from '../arc/features/new-hire-enrollment/member/hooks/useMemberGHLEmploymentId';
import { MemberPendingEnrollmentNotification } from '../notification/member-pending-enrollment-notification';
import { MyCpgMidYearNotification } from '../notification/mycpg-midyear-notification';
import { useGetMidYearPendingApproval } from '../../Global/hooks/useGetMidYearPendingApproval';
import { MycpgMidyearPendingApprovalNotification } from '../notification/mycpg-midyear-pending-approval-notification';
import { GetBottomSectionMessage } from './helpers/pods-helper-functions';
import { setUserContext } from '../../global/ducks/app-context';
import { UserContext } from '../../global/models/app-context-model';


interface Props {
    podsSettingsId: string;
    dashboardMode?: boolean;
    appConfig: TcsmAppConfigs;
}

export function MyCpgLandingPage(props: Props) {

    const [dashboardUser, setDashboardUser] = React.useState(null);
    const app = useContext(DemographicsContext);
    const { podsSettingsId, dashboardMode, appConfig } = props;
    let guid = useSelector<TcsmStates, string>(state => state.authenticatedState?.clientGuid);
    useMemberGHLEmploymentId();
    const pendingForApprovalEnrollment = useGetMidYearPendingApproval();
    const language = useSelector<TcsmStates, string>(state => state.globalization?.currentlanguage);
    const dispatch = useDispatch();
    const podsState = useSelector<TcsmStates, MyCpgPodsState>(state => state.mycpgPodsState);
    const settings = useSelector<TcsmStates, MyCpgPodsSettings>(state => state.epiProps?.podsSettingsState?.epiContent);
    const siteSettings = useSelector<TcsmStates, TcsmSiteSettingsProps>(state => state.epiProps?.siteSettingsState?.siteSettings);
    const detailPageType = useQuery()?.get('type');
    const notificationPath = useBasePath(`${AccountInfoRoutes.MyCpgRoot}${ArcRoutes.Notifications}`);
    const newsUpdatesPath = useBasePath(`${AccountInfoRoutes.MyCpgRoot}${ArcRoutes.NewsUpdates}`);
    const newsUpdatesPageData = useEpiPageContent(newsUpdatesPath) as TcsmNewsUpdatesPage;

    const token = useDeserializedToken();

    let name = props.dashboardMode ? dashboardUser?.name : `${token?.person?.fn} ${token?.person?.ln}`;

    if (!name) {
        name = '';
    }

    const roleMessage = '';

    useDemographicsPerson(app);

    useEffect(() => {
        if (dashboardMode) return null;

        dispatch(setUserContext(UserContext.MY_CPG));

		ClearTcsmSessionStorage();
		dispatch(getMyCpgPodInfo(
			(data) => {
				dispatch(setPods(data?.data));
				document.cookie = `SHOW_PENSION_CALCULATOR=${data?.data?.db_data?.db_estimate_link}; path=/`;
				dispatch(getTcsmEpiProps(dispatch, podsSettingsId, { guid }, language, false, setPodsSettings));
			},
			() => {
				dispatch(setPodsError());
				dispatch(getTcsmEpiProps(dispatch, podsSettingsId, { guid }, language, false, setPodsSettings));
			},
        ));
        dispatch(getNotifications(token?.person?.guid,
            (response) => dispatch(setNotifications(response?.data?.filter(a => a.notification_status === NotificationStatus.INITIAL || a.notification_status === NotificationStatus.DISMISSED))),
            () => dispatch(apiErrorClear()),
        ));

        return function cleanup() {
            dispatch(setNotifications([]));
        };
	}, []);


    useEffect(() => {
        if (!dashboardMode) return;

        guid = dashboardUser?.clientGuid;
        const roleMatrix = encodeURI(dashboardUser?.roleMatrix);

        if (props?.podsSettingsId) {
            dispatch(getTcsmEpiProps(dispatch, props?.podsSettingsId, { guid, roleMatrix }, language, false, setPodsSettings));
        }
    }, [dashboardUser]);

    if ((!dashboardMode && !podsState?.podsLoaded) || !settings) {
        return <LoadingCircle />;
    }

    if (detailPageType) {
        return <MyCpgPodsDetailPage settings={settings} appConfig={appConfig} />;
    }

    function UpdateDashboardData(du: DashboardUser) {
        setDashboardUser(du);
    }

    return (
        <>
            { !props.dashboardMode || <TcsmDashboardUsers setUserCallback={(u) => UpdateDashboardData(u)} />}
            <PageContainer>
                <PortalLandingBanner
                    userFullName={name}
                    userRole={roleMessage}
                    userGreeting={siteSettings?.greetingText}
                    brandImage={settings?.brandImage}
                    brandImageAltText={settings?.brandImageAltText}
                    bannerImageUrl={settings?.bannerImageLink}
                    bannerImageAltText={settings?.bannerImageAltText}
                    bannerImage={settings?.bannerImage} />
                <MainContent>
                    {
                        pendingForApprovalEnrollment && pendingForApprovalEnrollment?.length > 0 ?
                            <MycpgMidyearPendingApprovalNotification appConfig={appConfig} /> 
                            : 
                            <MyCpgMidYearNotification appConfig={appConfig} />
                    }
                    <MemberPendingEnrollmentNotification settings={settings}/>
                    <DashboardNotifications notificationPath={notificationPath} />
                    <MyCpgHero settings={settings} />
                    <RenderPods dashboardMode={dashboardMode} noAccessMessage={GetBottomSectionMessage(settings, podsState)} />
                    <PortalGuidesAndResources guidesResourcesHeader={settings?.guidesResourcesHeader} guidesResourcesBlocks={settings?.guidesResourcesBlocks} />
                    <WysiwygWithReactLink content={settings?.bottomMessage} />
                </MainContent>
                <PortalSidebar
                    latestUpdatesHeader={settings?.latestUpdatesHeader}
                    sidebarInfoBoxHeader={settings?.sidebarInfoBoxHeader}
                    sidebarInfoBoxContent={replaceAll(settings?.sidebarInfoBoxContent, '{guid}', guid)}
                    newsUpdatesItems={newsUpdatesPageData?.newsUpdatesItems}
                    newsUpdatesPath={newsUpdatesPath}
                />
            </PageContainer>
        </>
    );
}

MyCpgLandingPage.defaultProps = {
    dashboardMode: false,
};